import { Controller } from '@hotwired/stimulus';
import { loadScript } from '@paypal/paypal-js';
import { patch, post } from '@rails/request.js';

export default class extends Controller {
  static targets = [
    'amount',
    'slider',
    'email',
    'confirmed',
    'withdrawalConfirmed',
    'activate',
    'zeroAfter',
    'paypal',
    'submit',
    'submitWrap',
    'submitHint',
    'pay',
    'payWrap',
    'payHint',
    'demoNotice',
    'codePrefix'
  ];

  static values = {
    clientId: String,
    prepareUrl: String,
    completeUrl: String,
    locale: String,
    ordersEnabled: Boolean,
    testPrefix: String,
    paidPrefix: String,
    messages: Object
  };

  connect() {
    this.renderedAmount = null;
    this.payIntent = false;
    this.onChange = () => this.refresh();
    this.amountTarget.addEventListener('input', this.onChange);
    this.emailTarget.addEventListener('input', this.onChange);
    this.confirmedTarget.addEventListener('change', this.onChange);
    this.withdrawalConfirmedTarget.addEventListener('change', this.onChange);
    this.refresh();
  }

  disconnect() {
    this.amountTarget.removeEventListener('input', this.onChange);
    this.emailTarget.removeEventListener('input', this.onChange);
    this.confirmedTarget.removeEventListener('change', this.onChange);
    this.withdrawalConfirmedTarget.removeEventListener('change', this.onChange);
  }

  syncFromSlider() {
    this.amountTarget.value = this.sliderTarget.value;
    this.refresh();
  }

  formatAmount() {
    this.amountTarget.value = this.formatEuros(this.amountNumber());
    this.refresh();
  }

  startPayment() {
    if (!this.canPay()) return;

    this.payIntent = true;
    this.refresh();
  }

  refresh() {
    this.paintSlider();
    this.renderAmount();

    const amount = this.amountNumber();
    const previewOnly = !this.ordersEnabledValue;
    const paidPath = amount > 0;
    const ready = this.canPay();
    if (!ready) this.payIntent = false;

    const showPayButton = paidPath && !previewOnly && !this.payIntent;
    const showPaypal = !previewOnly && paidPath && ready && this.payIntent;

    this.updateCodePrefix(paidPath);
    this.updateSubmitButton(!paidPath, ready);
    this.updatePayButton(showPayButton, ready);
    this.paypalTarget.hidden = !showPaypal;
    if (this.hasDemoNoticeTarget)
      this.demoNoticeTarget.hidden = !(previewOnly && paidPath);

    if (!showPaypal) {
      this.clearPaypal();
      return;
    }

    if (this.renderedAmount === this.amountString()) return;

    this.clearPaypal();
    this.renderedAmount = this.amountString();
    this.renderPaypal();
  }

  updateCodePrefix(paidPath) {
    if (!this.hasCodePrefixTarget) return;

    const testPrefix = this.testPrefixValue || 'BOBBY-TEST-';
    const paidPrefix = this.paidPrefixValue || testPrefix;
    this.codePrefixTarget.textContent = paidPath ? paidPrefix : testPrefix;
  }

  updateSubmitButton(visible, ready) {
    if (!this.hasSubmitTarget) return;

    if (this.hasSubmitWrapTarget) this.submitWrapTarget.hidden = !visible;
    else this.submitTarget.hidden = !visible;

    this.submitTarget.disabled = !ready;

    if (this.hasSubmitHintTarget) {
      const hints = this.payHints();
      this.submitHintTarget.hidden = ready || hints.length === 0;
      this.submitHintTarget.textContent = hints.join(' ');
    }
  }

  updatePayButton(visible, ready) {
    if (!this.hasPayTarget || !this.hasPayWrapTarget) return;

    this.payWrapTarget.hidden = !visible;
    this.payTarget.disabled = !ready;

    if (this.hasPayHintTarget) {
      const hints = this.payHints();
      this.payHintTarget.hidden = ready || hints.length === 0;
      this.payHintTarget.textContent = hints.join(' ');
    }
  }

  payHints() {
    const hints = [];
    if (!this.emailFilled() && !this.termsAccepted())
      hints.push(this.message('email_and_terms'));
    else if (!this.emailFilled()) hints.push(this.message('email'));
    else if (!this.termsAccepted()) hints.push(this.message('terms'));
    return hints;
  }

  canPay() {
    return this.emailFilled() && this.termsAccepted();
  }

  termsAccepted() {
    return this.confirmedTarget.checked && this.withdrawalConfirmedTarget.checked;
  }

  emailFilled() {
    const value = String(this.emailTarget.value || '').trim();
    return value.includes('@') && value.includes('.');
  }

  async renderPaypal() {
    if (!this.clientIdValue) {
      this.showMessage(this.message('not_configured'));
      return;
    }

    let paypal;
    try {
      paypal = await loadScript({
        'client-id': this.clientIdValue,
        currency: 'EUR',
        intent: 'capture',
        locale: this.localeValue === 'en' ? 'en_US' : 'de_DE'
      });
    } catch (error) {
      console.error('failed to load the PayPal JS SDK script', error);
    }

    if (!paypal || this.renderedAmount !== this.amountString()) return;

    try {
      await paypal
        .Buttons({
          createOrder: async (_data, actions) => {
            if (!this.emailTarget.value) {
              this.showMessage(this.message('email_required'));
              throw new Error('email missing');
            }

            const orderId = await actions.order.create({
              purchase_units: [
                {
                  custom_id: 'wertgutschein',
                  amount: { value: this.amountString() }
                }
              ]
            });

            const response = await post(this.prepareUrlValue, {
              body: {
                email: this.emailTarget.value,
                amount: this.amountString(),
                paypal_order_id: orderId,
                // Checkbox is "voucher inactive"; unchecked means activate.
                activate_voucher: this.hasActivateTarget && !this.activateTarget.checked,

                zero_after: !this.hasZeroAfterTarget || this.zeroAfterTarget.checked
              },
              responseKind: 'json'
            });

            if (!response.ok) {
              const payload = await response.json;
              this.showMessage(payload.error || this.message('prepare_failed'));
              throw new Error('prepare failed');
            }

            return orderId;
          },
          onApprove: async (data) => {
            const response = await patch(this.completeUrlValue, {
              body: { paypal_order_id: data.orderID },
              responseKind: 'json'
            });
            const payload = await response.json;

            if (!response.ok || !payload.success) {
              this.showMessage(payload.error || this.message('payment_failed'));
              return;
            }

            window.location.href = payload.redirect_url;
          },
          onError: () => {
            this.showMessage(this.message('payment_failed'));
          }
        })
        .render(this.paypalTarget);

      this.paypalRendered = true;
    } catch (error) {
      console.error('failed to render the PayPal Buttons', error);
      this.showMessage(this.message('load_failed'));
    }
  }

  clearPaypal() {
    this.renderedAmount = null;
    this.paypalRendered = false;
    this.paypalTarget.replaceChildren();
  }

  showMessage(text) {
    const message = document.createElement('p');
    message.className = 'form__error';
    message.textContent = text;
    this.paypalTarget.replaceChildren(message);
    this.renderedAmount = null;
  }

  message(key) {
    return this.messagesValue?.[key] || '';
  }

  paintSlider() {
    if (!this.hasSliderTarget) return;

    const slider = this.sliderTarget;
    const min = Number(slider.min);
    const max = Number(slider.max);
    const amount = this.amountNumber();
    const clamped = Math.min(max, Math.max(min, amount));
    slider.value = String(clamped);

    const ratio = max === min ? 0 : (Number(slider.value) - min) / (max - min);
    const range = slider.closest('.voucher-shop__range') || slider;
    range.style.setProperty('--p', String(ratio));
  }

  renderAmount() {
    if (!this.hasAmountTarget || document.activeElement === this.amountTarget) return;

    this.amountTarget.value = this.formatEuros(this.amountNumber());
  }

  formatEuros(value) {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: false
    }).format(value);
  }

  amountNumber() {
    const raw = String(this.amountTarget.value || '')
      .trim()
      .replace(',', '.');
    const value = Number(raw);
    return Number.isFinite(value) ? value : 0;
  }

  amountString() {
    return this.amountNumber().toFixed(2);
  }
}
